import DOMPurify from 'dompurify'
import { queryClient } from 'v2/configs/rq-setup'
import { UserRole } from 'v2/types/api/auth'
import type { RActiveConversation } from 'v2/types/api/conversations'
import type { RRole, RUser } from 'v2/types/api/settings'
import dayjs from 'v2/utils/dayjs'
import { conversationQueryKeys } from './query-key-management'

// INR groups as lakh/crore (12,34,567) via en-IN; everything else en-US.
export const currencyLocale = (currency: string) => (currency === 'INR' ? 'en-IN' : 'en-US')

export const currencyFormatter = (
  currency: string,
  showDecimals = true,
  options: Omit<Intl.NumberFormatOptions, 'style'> = {},
) =>
  new Intl.NumberFormat(currencyLocale(currency), {
    style: 'currency',
    currency: currency || 'USD',
    currencyDisplay: 'symbol',
    minimumFractionDigits: showDecimals ? 2 : 0,
    ...options,
  })

export const formatCurrency = (
  amount: number,
  currency: string,
  showDecimals = true,
  options: Omit<Intl.NumberFormatOptions, 'style'> = {},
): string => currencyFormatter(currency, showDecimals, options).format(amount || 0)

export const performCallbackWithConversation = (
  convNumber: string,
  callback: (conv: RActiveConversation) => void,
) => {
  queueMicrotask(() => {
    const convs = queryClient.getQueryData<RActiveConversation[]>(conversationQueryKeys().active)
    const conversation = convs?.find((conv) => conv.conversation_number === convNumber)
    if (conversation) callback(conversation)
  })
}

export const exportRecordingMessage = (email: string) =>
  `A link to download the call recording will be sent to ${email} in some time. Check your inbox after a while.`

/**
 * Role name to display label mapping
 */
const ROLE_LABELS: Record<string, string> = {
  [UserRole.administrator]: 'Administrator',
  [UserRole.agent]: 'Human specialist',
  [UserRole.supervisor]: 'Supervisor',
  [UserRole.finance_analyst]: 'Finance analyst',
  [UserRole.developer]: 'Developer',
  [UserRole.support]: 'Support',
  [UserRole.ucc_reviewer]: 'UCC reviewer',
}

/**
 * Get display label for a role name
 * @param roleName - The role name from the API
 * @returns The display label for the role
 */
export const getRoleLabel = (roleName: string): string => {
  return ROLE_LABELS[roleName] || roleName.replace(/_/g, ' ')
}

export type RoleOption = { label: string; value: string }

/**
 * Assignable member roles in display order; fallback when backend roles aren't available.
 */
const DEFAULT_ASSIGNABLE_ROLES: string[] = [
  UserRole.administrator,
  UserRole.supervisor,
  UserRole.agent,
  UserRole.finance_analyst,
  UserRole.developer,
  UserRole.support,
]

/**
 * Build the assignable-role options for the Team-setup member picker/filter from the roles API.
 * Only predefined roles are assignable; falls back to the known role set when none are returned.
 * @param roles - Roles from the backend (GET /rbac/roles); may be undefined while loading
 * @param includeAgentRole - Include the `agent` ("Human specialist") role (org picker: only when
 *   human-specialist mode is on; filter: always, so any held role is filterable)
 * @returns Options ({ label, value: role name }) for the role picker/filter
 */
export const buildRoleOptions = (
  roles: RRole[] | undefined,
  includeAgentRole: boolean,
): RoleOption[] => {
  const predefinedNames: string[] = []
  for (const role of roles ?? []) {
    if (role.is_predefined) predefinedNames.push(role.name)
  }
  const roleNames = predefinedNames.length ? predefinedNames : DEFAULT_ASSIGNABLE_ROLES
  const options: RoleOption[] = []
  for (const name of roleNames) {
    if (name === UserRole.agent && !includeAgentRole) continue
    options.push({ label: getRoleLabel(name), value: name })
  }
  return options
}

/**
 * Get all roles for a user
 * @param user - The user object
 * @returns Array of role objects
 */
export const getUserRoles = (user: RUser | null): { id: string; name: string }[] => {
  if (!user) return []
  return user.roles || []
}

/**
 * Get role names as an array
 * @param user - The user object
 * @returns Array of role name strings
 */
export const getUserRoleNames = (user: RUser | null): string[] => {
  return getUserRoles(user).map((role) => role.name)
}

/**
 * Check if user has a specific role
 * @param user - The user object
 * @param roleName - The role name to check
 * @returns boolean
 */
export const userHasRole = (user: RUser | null, roleName: string): boolean => {
  return getUserRoleNames(user).includes(roleName)
}

/**
 * Route meta type for role checking
 */
interface RouteMeta {
  roles?: UserRole[]
}

/**
 * Check if route meta has a specific role (for route configuration)
 * @param meta - The route meta object containing roles
 * @param roleName - The role name to check
 * @returns boolean
 */
export const hasRole = (meta?: RouteMeta, roleName?: UserRole): boolean => {
  if (!roleName) return false
  const roles = meta?.roles || []
  return roles.includes(roleName)
}

/**
 * Check if route meta has only agent role (for route restrictions)
 * @param meta - The route meta object containing roles
 * @returns boolean
 */
export const isOnlyAgentRole = (meta?: RouteMeta): boolean => {
  const roles = meta?.roles || []
  return roles.length === 1 && roles[0] === UserRole.agent
}

export const getTimeOptions = (isStartTime = false, useModernEndTime = false) => {
  const timeOptions = []
  const isModernEndTime = !isStartTime && useModernEndTime
  const maxHour = isModernEndTime ? 23 : isStartTime ? 23 : 24

  for (let hour = 0; hour <= maxHour; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      // Skip conditions
      if (hour === 24 && minute > 0) break // Legacy: no minutes after 24:00
      if (isModernEndTime && hour === 0 && minute === 0) continue // Modern: skip 12:00 AM

      const isAM = hour < 12 || hour === 24
      const hour12 = hour === 0 || hour === 24 ? 12 : hour > 12 ? hour - 12 : hour
      const label = `${hour12.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')} ${isAM ? 'AM' : 'PM'}`
      const value =
        hour === 24
          ? '24:00'
          : `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`

      timeOptions.push({ label, value })
    }
  }

  // Add 11:59 PM as final option for modern end time
  if (isModernEndTime) {
    timeOptions.push({ label: '11:59 PM', value: '23:59' })
  }

  return timeOptions
}

export const formatToastDescription = (text: string | JSX.Element | JSX.Element[] | undefined) => {
  if (!text) return 'Something went wrong'
  if (typeof text !== 'string') return text //for JSX elements
  // Check if text contains HTML tags
  const containsHtml = /<[a-z][\s\S]*>/i.test(text)

  if (containsHtml) {
    return (
      <div
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(text, {
            ADD_ATTR: ['target'], // Allow target attribute to be preserved
          }),
        }}
      />
    )
  }

  return text
}

export const formatMillis = (ms: number): string => {
  if (!Number.isFinite(ms)) return ''
  if (ms < 1000) {
    return `${Math.round(ms)} ms`
  }
  return `${(ms / 1000).toFixed(2)} s`
}

/**
 * Render a "5d 2h left" / "2h 30m left" / "5m left" / "Expired" string for a
 * unix-seconds expiry timestamp. Used for support-session countdowns.
 */
export const formatTimeLeft = (expiresAtSec: number): string => {
  const now = dayjs()
  const expiry = dayjs.unix(expiresAtSec)
  if (!expiry.isValid() || expiry.isBefore(now)) return 'Expired'

  const diff = dayjs.duration(expiry.diff(now))
  const days = Math.floor(diff.asDays())
  const hours = diff.hours()
  const minutes = diff.minutes()

  if (days >= 1) return `${days}d ${hours}h left`
  if (hours >= 1) return `${hours}h ${minutes}m left`
  return `${Math.max(minutes, 1)}m left`
}
